import React from 'react';
import { MapPin, Heart, Target, Utensils, Clock, Globe, Brain, Smile } from 'lucide-react';
import { UserPersonalizationProfile } from '../../types/users';

interface StepProps {
  profile: Partial<UserPersonalizationProfile>;
  updateProfile: (updates: Partial<UserPersonalizationProfile>) => void;
}

export const LocationStep: React.FC<StepProps> = ({ profile, updateProfile }) => {
  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">Where are you located?</h3>
        <p className="text-slate-600 text-sm">This helps us suggest foods common in your region</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
        {countries.map((country) => (
          <button
            key={country.code}
            onClick={() => updateProfile({ country: country.code })}
            className={`p-3 border-2 rounded-lg transition-all text-left ${
              profile.country === country.code
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <span className="text-xl">{country.flag}</span>
              <span className="text-sm font-medium">{country.name}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const HealthGoalsStep: React.FC<StepProps> = ({ profile, updateProfile }) => {
  const healthGoals = [
    { id: 'diabetes_management', label: 'Diabetes Management', icon: '🩺', color: 'bg-red-100 border-red-300' },
    { id: 'weight_loss', label: 'Weight Loss', icon: '⚖️', color: 'bg-green-100 border-green-300' },
    { id: 'heart_health', label: 'Heart Health', icon: '❤️', color: 'bg-red-100 border-red-300' },
    { id: 'blood_pressure', label: 'Blood Pressure', icon: '🩸', color: 'bg-blue-100 border-blue-300' },
    { id: 'cholesterol', label: 'Cholesterol Management', icon: '🧪', color: 'bg-purple-100 border-purple-300' },
    { id: 'nutrition', label: 'Better Nutrition', icon: '🥗', color: 'bg-green-100 border-green-300' },
    { id: 'energy', label: 'More Energy', icon: '⚡', color: 'bg-yellow-100 border-yellow-300' },
    { id: 'sleep', label: 'Better Sleep', icon: '😴', color: 'bg-indigo-100 border-indigo-300' }
  ];

  const toggleGoal = (goalId: string) => {
    const currentGoals = profile.healthGoals || [];
    const newGoals = currentGoals.includes(goalId)
      ? currentGoals.filter(g => g !== goalId)
      : [...currentGoals, goalId];
    updateProfile({ healthGoals: newGoals });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">What are your health goals?</h3>
        <p className="text-slate-600 text-sm">Select any that apply - this helps us personalize your experience</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {healthGoals.map((goal) => (
          <button
            key={goal.id}
            onClick={() => toggleGoal(goal.id)}
            className={`p-4 border-2 rounded-lg transition-all text-left ${
              (profile.healthGoals || []).includes(goal.id)
                ? `${goal.color} border-opacity-60`
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{goal.icon}</span>
              <span className="text-sm font-medium">{goal.label}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const EatingHabitsStep: React.FC<StepProps> = ({ profile, updateProfile }) => {
  const updateHabits = (field: string, value: any) => {
    updateProfile({
      eatingHabits: {
        ...profile.eatingHabits,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">Tell us about your eating habits</h3>
        <p className="text-slate-600 text-sm">This helps us suggest the right foods at the right times</p>
      </div>

      <div className="space-y-6">
        {/* Meals per day */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            How many times do you usually eat per day?
          </label>
          <div className="grid grid-cols-4 gap-2">
            {[2, 3, 4, 5].map(num => (
              <button
                key={num}
                onClick={() => updateHabits('mealsPerDay', num)}
                className={`p-3 border-2 rounded-lg transition-all ${
                  profile.eatingHabits?.mealsPerDay === num
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-slate-200 hover:border-orange-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-lg font-bold">{num}</div>
                  <div className="text-xs text-slate-600">
                    {num === 2 ? 'times' : num === 3 ? 'times' : num === 4 ? 'times' : 'times+'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Snacking habits */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Do you snack between meals?
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'never', label: 'Never', icon: '🚫' },
              { value: 'occasionally', label: 'Sometimes', icon: '🍎' },
              { value: 'frequently', label: 'Often', icon: '🍿' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => updateHabits('snacking', option.value)}
                className={`p-3 border-2 rounded-lg transition-all text-center ${
                  profile.eatingHabits?.snacking === option.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-slate-200 hover:border-orange-300'
                }`}
              >
                <div className="text-lg">{option.icon}</div>
                <div className="text-sm font-medium">{option.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Cooking frequency */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            How often do you cook at home?
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'rarely', label: 'Rarely cook', icon: '🍕' },
              { value: 'sometimes', label: 'Sometimes', icon: '🍳' },
              { value: 'often', label: 'Often', icon: '👨‍🍳' },
              { value: 'daily', label: 'Almost daily', icon: '👩‍🍳' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => updateHabits('cookingFrequency', option.value)}
                className={`p-3 border-2 rounded-lg transition-all text-center ${
                  profile.eatingHabits?.cookingFrequency === option.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-slate-200 hover:border-orange-300'
                }`}
              >
                <div className="text-lg">{option.icon}</div>
                <div className="text-sm font-medium">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FoodPreferencesStep: React.FC<StepProps> = ({ profile, updateProfile }) => {
  const foodCategories = [
    { id: 'fruits', label: 'Fruits', icon: '🍎' },
    { id: 'vegetables', label: 'Vegetables', icon: '🥕' },
    { id: 'grains', label: 'Grains & Cereals', icon: '🌾' },
    { id: 'proteins', label: 'Proteins', icon: '🍖' },
    { id: 'dairy', label: 'Dairy', icon: '🥛' },
    { id: 'nuts', label: 'Nuts & Seeds', icon: '🥜' },
    { id: 'seafood', label: 'Seafood', icon: '🐟' },
    { id: 'sweets', label: 'Sweets', icon: '🍰' }
  ];

  const cuisines = [
    { id: 'american', label: 'American', icon: '🍔' },
    { id: 'italian', label: 'Italian', icon: '🍝' },
    { id: 'mexican', label: 'Mexican', icon: '🌮' },
    { id: 'asian', label: 'Asian', icon: '🍜' },
    { id: 'mediterranean', label: 'Mediterranean', icon: '🫒' },
    { id: 'indian', label: 'Indian', icon: '🍛' }
  ];

  const toggleCategory = (categoryId: string) => {
    const current = profile.foodPreferences?.favoriteCategories || [];
    const updated = current.includes(categoryId)
      ? current.filter(c => c !== categoryId)
      : [...current, categoryId];
    
    updateProfile({
      foodPreferences: {
        ...profile.foodPreferences,
        favoriteCategories: updated
      }
    });
  };

  const toggleCuisine = (cuisineId: string) => {
    const current = profile.foodPreferences?.favoriteCuisines || [];
    const updated = current.includes(cuisineId)
      ? current.filter(c => c !== cuisineId)
      : [...current, cuisineId];
    
    updateProfile({
      foodPreferences: {
        ...profile.foodPreferences,
        favoriteCuisines: updated
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Utensils className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">What foods do you enjoy?</h3>
        <p className="text-slate-600 text-sm">Select your favorites so we can suggest similar foods</p>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Food Categories</h4>
          <div className="grid grid-cols-2 gap-2">
            {foodCategories.map(category => (
              <button
                key={category.id}
                onClick={() => toggleCategory(category.id)}
                className={`p-3 border-2 rounded-lg transition-all text-left ${
                  (profile.foodPreferences?.favoriteCategories || []).includes(category.id)
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{category.icon}</span>
                  <span className="text-sm font-medium">{category.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Favorite Cuisines</h4>
          <div className="grid grid-cols-2 gap-2">
            {cuisines.map(cuisine => (
              <button
                key={cuisine.id}
                onClick={() => toggleCuisine(cuisine.id)}
                className={`p-3 border-2 rounded-lg transition-all text-left ${
                  (profile.foodPreferences?.favoriteCuisines || []).includes(cuisine.id)
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{cuisine.icon}</span>
                  <span className="text-sm font-medium">{cuisine.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AIPersonalityStep: React.FC<StepProps> = ({ profile, updateProfile }) => {
  const personalities = [
    {
      id: 'encouraging',
      label: 'Encouraging Coach',
      description: 'Supportive and motivational',
      icon: '💪',
      example: '"Great job logging that meal! You\'re building healthy habits!"'
    },
    {
      id: 'professional',
      label: 'Clinical Professional',
      description: 'Medical and fact-based',
      icon: '👩‍⚕️',
      example: '"Your carbohydrate intake is within recommended ranges."'
    },
    {
      id: 'casual',
      label: 'Friendly Buddy',
      description: 'Casual and conversational',
      icon: '😊',
      example: '"Hey! That breakfast looks delicious and nutritious!"'
    },
    {
      id: 'motivational',
      label: 'Fitness Motivator',
      description: 'High-energy and goal-focused',
      icon: '🔥',
      example: '"You\'re crushing your health goals! Keep that momentum!"'
    }
  ];

  const updateAI = (field: string, value: any) => {
    updateProfile({
      aiPreferences: {
        ...profile.aiPreferences,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">Choose your AI assistant style</h3>
        <p className="text-slate-600 text-sm">How would you like ZippyHealth AI to communicate with you?</p>
      </div>

      <div className="space-y-4">
        {personalities.map(personality => (
          <button
            key={personality.id}
            onClick={() => updateAI('personalityStyle', personality.id)}
            className={`w-full p-4 border-2 rounded-lg transition-all text-left ${
              profile.aiPreferences?.personalityStyle === personality.id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <span className="text-2xl">{personality.icon}</span>
              <div className="flex-1">
                <div className="font-medium text-slate-800">{personality.label}</div>
                <div className="text-sm text-slate-600 mb-2">{personality.description}</div>
                <div className="text-xs italic text-slate-500 bg-slate-50 p-2 rounded">
                  {personality.example}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          <strong>Don't worry!</strong> You can always change this later in settings.
        </p>
      </div>
    </div>
  );
}; 
